import { useState } from 'react';
import { Form, Button, Row, Col, InputGroup, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWrench,
  faEuroSign,
  faShieldAlt,
  faReceipt,
  faSave,
  faCreditCard,
  faMicrochip,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { formatCurrency } from '../../../utils/formatCurrency';
import { REPAIR_TYPES } from './repairTypes';

export default function Calculator({ settings, onSave, isSaving, initialData, onImportToDevice }) {
  const { t } = useTranslation();

  const [partCostInput, setPartCostInput] = useState(initialData ? String(initialData.partCost || '') : '');
  const [repairType, setRepairType] = useState(() => {
    const initial = initialData ? String(initialData.repairType || '') : '';
    return REPAIR_TYPES.includes(initial) ? initial : (initial ? 'Altro' : '');
  });
  const [customRepairType, setCustomRepairType] = useState(() => {
    const initial = initialData ? String(initialData.repairType || '') : '';
    return REPAIR_TYPES.includes(initial) ? '' : initial;
  });
  const [difficulty, setDifficulty] = useState(initialData ? initialData.difficulty : 'medium');
  const [useCard, setUseCard] = useState(initialData ? initialData.useCard : (settings.defaultCardPayment ?? false));
  const [isManualOverride, setIsManualOverride] = useState(false);
  const [manualTotalInput, setManualTotalInput] = useState('');

  const partCostValue = parseFloat(partCostInput) || 0;

  // Tipo di riparazione effettivo: se "Altro", usa il testo personalizzato.
  const effectiveRepairType = repairType === 'Altro'
    ? (customRepairType.trim() || 'Altro')
    : repairType;

  let finalTotal = 0;
  let paymentFee = 0;
  let subTotal = 0;
  let ivaAmount = 0;
  let netProfit = 0;
  let calculatedMarginForDisplay = 0;

  if (isManualOverride) {
    const overrideValue = parseFloat(manualTotalInput) || 0;
    finalTotal = overrideValue;

    if (useCard && overrideValue > 0) {
      paymentFee = overrideValue * ((settings.cardFeePercentage || 0) / 100) + (settings.cardFixedFee || 0);
    }

    const grossReceived = overrideValue - paymentFee;
    subTotal = grossReceived / (1 + (settings.ivaPercentage || 0) / 100);
    ivaAmount = grossReceived - subTotal;
    netProfit = subTotal - partCostValue;
    calculatedMarginForDisplay = netProfit;
  } else {
    let isPercentage = settings.marginType === 'percentage';
    if (settings.marginType === 'auto') {
      isPercentage = partCostValue > (settings.autoMarginThreshold || 0);
    }

    const currentMargin = isPercentage
      ? (partCostValue * ((difficulty === 'easy' ? settings.marginEasyPct : difficulty === 'medium' ? settings.marginMediumPct : settings.marginHardPct) || 0)) / 100
      : (difficulty === 'easy' ? settings.marginEasy : difficulty === 'medium' ? settings.marginMedium : settings.marginHard) || 0;

    calculatedMarginForDisplay = currentMargin;
    subTotal = partCostValue + currentMargin;
    ivaAmount = subTotal * ((settings.ivaPercentage || 0) / 100);
    const baseTotal = subTotal + ivaAmount;

    finalTotal = baseTotal;

    if (useCard) {
      const feePct = (settings.cardFeePercentage || 0) / 100;
      const fixedFee = settings.cardFixedFee || 0;
      if (feePct < 1) {
        finalTotal = (baseTotal + fixedFee) / (1 - feePct);
        paymentFee = finalTotal - baseTotal;
      }
    }

    netProfit = currentMargin || 0;
  }

  const difficultyOptions = [
    { key: 'easy', icon: faWrench },
    { key: 'medium', icon: faWrench },
    { key: 'hard', icon: faShieldAlt }
  ];

  const hasQuote = (isManualOverride && finalTotal > 0) || (!isManualOverride && partCostValue > 0);

  return (
    <Row className="g-4">
      <Col lg={7}>
        <div className="repaircalc-section">
          <h5 className="mb-4">
            <FontAwesomeIcon icon={faMicrochip} className="text-primary me-2" />
            {t('repaircalc.calculator.repairData')}
          </h5>

          <Form.Group className="mb-4">
            <Form.Label>{t('repaircalc.calculator.repairType')}</Form.Label>
            <Form.Select
              value={repairType}
              onChange={(e) => setRepairType(e.target.value)}
            >
              <option value="">{t('repaircalc.calculator.selectRepairType')}</option>
              {REPAIR_TYPES.map((v) => (
                <option key={v} value={v}>{t(`models.device.problems.${v}`)}</option>
              ))}
            </Form.Select>
            {repairType === 'Altro' && (
              <Form.Control
                type="text"
                className="mt-2"
                value={customRepairType}
                onChange={(e) => setCustomRepairType(e.target.value)}
                placeholder={t('repaircalc.calculator.repairTypePlaceholder')}
              />
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('repaircalc.calculator.partCost')}</Form.Label>
            <InputGroup>
              <InputGroup.Text><FontAwesomeIcon icon={faEuroSign} /></InputGroup.Text>
              <Form.Control
                type="number"
                className="no-spinner"
                value={partCostInput}
                onChange={(e) => setPartCostInput(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0.00"
              />
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('repaircalc.calculator.difficulty')}</Form.Label>
            <Row className="g-2">
              {difficultyOptions.map((opt) => (
                <Col key={opt.key}>
                  <button
                    type="button"
                    className={`difficulty-btn diff-${opt.key} ${difficulty === opt.key && !isManualOverride ? 'active' : ''}`}
                    onClick={() => { setDifficulty(opt.key); setIsManualOverride(false); }}
                  >
                    <FontAwesomeIcon icon={opt.icon} className="diff-icon" />
                    <span>{t(`repaircalc.calculator.${opt.key}`)}</span>
                  </button>
                </Col>
              ))}
            </Row>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('repaircalc.calculator.paymentMethod')}</Form.Label>
            <Card
              className={`payment-card ${useCard ? 'border-primary' : ''}`}
              onClick={() => setUseCard(!useCard)}
            >
              <Card.Body className="py-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCreditCard} className={`me-3 fs-5 ${useCard ? 'text-primary' : 'text-muted'}`} />
                  <div>
                    <div className="fw-medium">{t('repaircalc.calculator.cardPayment')}</div>
                    <div className="text-muted small">
                      {t('repaircalc.calculator.cardFees')}: {settings.cardFeePercentage}% + {settings.cardFixedFee}€
                    </div>
                  </div>
                </div>
                <div className={`round-selector ${useCard ? 'active' : ''}`}>
                  {useCard && <span className="round-dot" />}
                </div>
              </Card.Body>
            </Card>
          </Form.Group>

          <hr />

          <Form.Group>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-medium">{t('repaircalc.calculator.manualPrice')}</div>
                <div className="text-muted small">{t('repaircalc.calculator.manualPriceDesc')}</div>
              </div>
              <Form.Check
                type="switch"
                id="manual-override-switch"
                checked={isManualOverride}
                onChange={(e) => {
                  setIsManualOverride(e.target.checked);
                  if (e.target.checked && !manualTotalInput) {
                    setManualTotalInput(finalTotal > 0 ? finalTotal.toFixed(2) : '');
                  }
                }}
                className="custom-switch"
              />
            </div>
            {isManualOverride && (
              <InputGroup className="mt-3">
                <InputGroup.Text><FontAwesomeIcon icon={faEuroSign} /></InputGroup.Text>
                <Form.Control
                  type="number"
                  className="no-spinner"
                  value={manualTotalInput}
                  onChange={(e) => setManualTotalInput(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="120.00"
                />
              </InputGroup>
            )}
          </Form.Group>
        </div>
      </Col>

      <Col lg={5}>
        <div className="receipt-card">
          <div className="d-flex align-items-center mb-4">
            <FontAwesomeIcon icon={faReceipt} className="me-2" />
            <span className="receipt-heading">{t('repaircalc.calculator.estimatedQuote')}</span>
            {isManualOverride && (
              <span className="badge ms-auto" style={{ background: 'rgba(59,130,246,0.2)', color: '#93C5FD' }}>
                {t('repaircalc.calculator.manual')}
              </span>
            )}
          </div>

          <div className="receipt-row">
            <span className="receipt-label">{t('repaircalc.calculator.partCostLabel')}</span>
            <span className="receipt-value">{formatCurrency(partCostValue, 'EUR')}</span>
          </div>
          {!isManualOverride && (
            <div className="receipt-row">
              <span className="receipt-label">
                {t('repaircalc.calculator.grossMargin')} ({difficultyLabel(difficulty)})
              </span>
              <span className="text-profit">+{formatCurrency(calculatedMarginForDisplay, 'EUR')}</span>
            </div>
          )}

          <hr className="receipt-divider" />

          <div className="receipt-row">
            <span className="receipt-label">{t('repaircalc.calculator.taxableBase')}</span>
            <span className="receipt-value">{formatCurrency(subTotal, 'EUR')}</span>
          </div>
          <div className="receipt-row">
            <span className="receipt-label">{t('repaircalc.calculator.vat')} ({settings.ivaPercentage || 0}%)</span>
            <span className="receipt-value">+{formatCurrency(ivaAmount, 'EUR')}</span>
          </div>
          {useCard && (
            <div className="receipt-row">
              <span className="receipt-label">{t('repaircalc.calculator.posFee')}</span>
              <span className="text-warning">{isManualOverride ? '-' : '+'}{formatCurrency(paymentFee, 'EUR')}</span>
            </div>
          )}

          <hr className="receipt-divider" />

          <div className="receipt-total mb-4">
            <span className="receipt-total-label">{t('repaircalc.calculator.clientTotal')}</span>
            <span className="receipt-total-value">{formatCurrency(finalTotal, 'EUR')}</span>
          </div>

          <div className="receipt-subheading mb-2">{t('repaircalc.calculator.marginSummary')}</div>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <span className="receipt-label">{t('repaircalc.calculator.netProfit')}</span>
            <span className={`fw-bold fs-5 ${netProfit >= 0 ? 'text-profit' : 'text-danger'}`}>
              {formatCurrency(netProfit, 'EUR')}
            </span>
          </div>

          {onSave && (
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                className="equal-width"
                onClick={() => onSave({
                  partCost: partCostValue,
                  difficulty,
                  useCard,
                  totalClient: finalTotal,
                  netProfit,
                  repairType: effectiveRepairType
                })}
                disabled={isSaving || !hasQuote}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {isSaving ? t('repaircalc.calculator.saving') : t('repaircalc.calculator.save')}
              </Button>
              {onImportToDevice && (
                <Button
                  variant="success"
                  className="equal-width"
                  onClick={() => onImportToDevice({
                    partCost: partCostValue,
                    difficulty,
                    useCard,
                    totalClient: finalTotal,
                    netProfit,
                    repairType: effectiveRepairType
                  })}
                  disabled={isSaving || !hasQuote}
                >
                  <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                  {t('repaircalc.calculator.importToDevice')}
                </Button>
              )}
            </div>
          )}
        </div>
      </Col>
    </Row>
  );

  function difficultyLabel(d) {
    if (d === 'easy') return t('repaircalc.calculator.easy');
    if (d === 'hard') return t('repaircalc.calculator.hard');
    return t('repaircalc.calculator.medium');
  }
}
